import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom'; // ← Agregar useNavigate
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Utensils, Clock, Heart, Calendar, Lightbulb, ExternalLink } from 'lucide-react';
import AddToWeeklyMenuDialog from '@/components/AddToWeeklyMenuDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipeDetail } from '@/hooks/useRecipeDetail';
import { foodFactService, FoodFactResponse } from '@/services/foodFactService';
import { Difficulty, MealCategory, Tag } from '@/types';
import { getCorrectImageUrl } from '@/utils/imageUtils'; // ← IMPORTAR ESTA FUNCIÓN

const RecipeDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate(); // ← Para redirección
  const [isFavorited, setIsFavorited] = useState(false);
  const [addToMenuOpen, setAddToMenuOpen] = useState(false);
  const [relevantFacts, setRelevantFacts] = useState<FoodFactResponse[]>([]);
  const [factsLoading, setFactsLoading] = useState(true);

  const { recipe, loading, error } = useRecipeDetail(id);
  // ✅ DEBUG TEMPORAL
  console.log('🔍 RecipeDetail Debug:', {
    recipeId: id,
    hasRecipe: !!recipe,
    rawImage: recipe?.image,
    imageUrl: getCorrectImageUrl(recipe?.image)
  });

  // ✅ CORREGIDO: Usar la misma función que RecipeCard
  const imageUrl = getCorrectImageUrl(recipe?.image);

  // Cargar facts relevantes cuando la receta esté disponible
  useEffect(() => {
    const loadRelevantFacts = async () => {
      if (!recipe?.id) return;

      try {
        setFactsLoading(true);
        const facts = await foodFactService.getRelevantFactsForRecipe(recipe.id);
        setRelevantFacts(facts);
      } catch (error) {
        console.error('Error loading relevant facts:', error);
        // No mostrar error al usuario - los facts son opcionales
      } finally {
        setFactsLoading(false);
      }
    };

    loadRelevantFacts();
  }, [recipe?.id]);

  // Redirigir si no está loggeado (para recetas privadas)
  React.useEffect(() => {
    if (!isLoggedIn && error?.includes('no tienes permisos')) {
      navigate('/login');
    }
  }, [error, isLoggedIn]);

  const toggleFavorite = () => {
    // TODO: Integrar con el sistema de favoritos real
    setIsFavorited(!isFavorited);
    toast({
      title: isFavorited ? "Eliminada de favoritos" : "Agregada a favoritos",
      description: `${recipe?.title} ${isFavorited ? 'fue eliminada de' : 'fue agregada a'} tus favoritos.`,
    });
  };

  // Helper para mostrar la dificultad en español
  const getDifficultyText = (difficulty: Difficulty) => {
    const difficultyMap = {
      [Difficulty.FÁCIL]: 'Fácil',
      [Difficulty.MEDIO]: 'Medio',
      [Difficulty.DIFÍCIL]: 'Difícil'
    };
    return difficultyMap[difficulty];
  };

  // Calcular tiempos estimados basados en el tiempo total
  const totalTime = parseInt(recipe?.time || '30');
  const prepTime = Math.max(10, totalTime - 15);
  const cookTime = Math.max(5, totalTime - prepTime);

  // Componente para mostrar un FoodFact individual
  const FoodFactCard = ({ fact }: { fact: FoodFactResponse; }) => (
    <Card key={fact.id} className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200 hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-full">
            <Lightbulb className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-lg text-green-800">{fact.title}</CardTitle>
            <CardDescription className="text-green-600">
              Dato nutricional relevante
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-green-700 leading-relaxed mb-4">{fact.fact}</p>
        <div className="flex items-center justify-between">
          <p className="text-sm text-green-600">Fuente: {fact.source}</p>
          {fact.sourceUrl && (
            <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700" asChild>
              <a href={fact.sourceUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />
                Ver fuente
              </a>
            </Button>
          )}
        </div>
        {fact.keywords && fact.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {fact.keywords.slice(0, 3).map((keyword, index) => (
              <Badge key={index} variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                #{keyword}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="text-lg">Cargando receta...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">
              {error || 'Receta no encontrada'}
            </h2>
            <p className="text-gray-600 mb-4">
              {error?.includes('pública')
                ? 'Esta receta es privada o no existe.'
                : 'La receta que buscas no existe o no tienes permisos para verla.'
              }
            </p>
            <Link to="/">
              <Button>Volver al inicio</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="h-4 w-4" />
          Volver a recetas
        </Link>

        {/* Recipe Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Image and Actions */}
          <div>
            <img
              src={imageUrl || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'}
              alt={recipe.title}
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />

            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleFavorite}
                  className={isFavorited ? 'text-red-500 border-red-200 bg-red-50' : ''}
                >
                  <Heart className={`h-4 w-4 mr-2 ${isFavorited ? 'fill-current' : ''}`} />
                  {isFavorited ? 'Favorito' : 'Agregar a favoritos'}
                </Button>

                {isLoggedIn && (
                  <Button
                    size="sm"
                    onClick={() => setAddToMenuOpen(true)}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Agregar al menú
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Recipe Info */}
          <div>
            <div className="mb-4">
              <Badge variant="secondary" className="mb-2">
                {recipe.category}
              </Badge>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{recipe.title}</h1>
              <p className="text-gray-600 leading-relaxed">
                {recipe.instructions?.split('.')[0] || 'Deliciosa receta cargada de sabor y nutrientes.'}
              </p>
            </div>

            {/* Metadata */}
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-500" />
                <span className="text-sm">{recipe.time}</span>
              </div>
              <div className="flex items-center gap-2">
                <Utensils className="h-5 w-5 text-gray-500" />
                <span className="text-sm">{getDifficultyText(recipe.difficulty)}</span>
              </div>
              {recipe.tags && recipe.tags.length > 0 && (
                <div className="flex items-center gap-1">
                  {recipe.tags.slice(0, 2).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Nutrition Facts */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Información Nutricional</CardTitle>
                <CardDescription>Por porción</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{recipe.calories}</div>
                    <div className="text-sm text-gray-600">Calorías</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{recipe.protein}g</div>
                    <div className="text-sm text-gray-600">Proteína</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{recipe.carbs}g</div>
                    <div className="text-sm text-gray-600">Carbohidratos</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{recipe.fat}g</div>
                    <div className="text-sm text-gray-600">Grasa</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preparation Time */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-xl font-bold text-blue-600">{prepTime} min</div>
                  <div className="text-sm text-gray-600">Preparación</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-xl font-bold text-orange-600">{cookTime} min</div>
                  <div className="text-sm text-gray-600">Cocción</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Food Facts Section */}
        {relevantFacts.length > 0 && (
          <div className="mt-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 rounded-full">
                <Lightbulb className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Datos Nutricionales Relevantes</h2>
                <p className="text-gray-600">
                  Información científica relacionada con los ingredientes de esta receta
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {relevantFacts.map((fact) => (
                <FoodFactCard key={fact.id} fact={fact} />
              ))}
            </div>

            <div className="text-center mt-6">
              <Button variant="outline" asChild>
                <Link to="/food-facts">
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Explorar más datos nutricionales
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Loading State for Facts */}
        {factsLoading && (
          <div className="mt-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 rounded-full">
                <Lightbulb className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Buscando datos nutricionales...</h2>
                <p className="text-gray-600">Analizando ingredientes para información relevante</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {[1, 2].map((n) => (
                <Card key={n} className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200 animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-green-200 rounded w-1/3 mb-2"></div>
                    <div className="h-6 bg-green-200 rounded w-2/3"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-4 bg-green-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-green-200 rounded w-3/4"></div>
                    <div className="h-4 bg-green-200 rounded w-1/2 mt-4"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No Facts Message */}
        {!factsLoading && relevantFacts.length === 0 && (
          <Card className="mt-8 mb-8 bg-amber-50 border-amber-200">
            <CardHeader>
              <CardTitle className="text-amber-800 flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                ¿Sabías que...?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-700 mb-4">
                Esta receta contiene ingredientes nutritivos que benefician tu salud.
                Próximamente agregaremos información científica específica sobre sus propiedades.
              </p>
              <Button variant="outline" asChild>
                <Link to="/food-facts">
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Descubre datos nutricionales
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Ingredients and Instructions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Ingredients */}
          <Card>
            <CardHeader>
              <CardTitle>Ingredientes</CardTitle>
              <CardDescription>
                {recipe.ingredients?.length || 0} ingredientes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recipe.ingredients && recipe.ingredients.length > 0 ? (
                <ul className="space-y-3">
                  {recipe.ingredients.map((ingredient, index) => (
                    <li key={index} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{ingredient}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Utensils className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No se especificaron ingredientes</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instrucciones</CardTitle>
              <CardDescription>
                Pasos de preparación
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recipe.instructions ? (
                <div className="space-y-4">
                  {recipe.instructions.split('\n').filter(step => step.trim()).map((paragraph, index) => (
                    <div key={index} className="flex gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <p className="text-gray-700 leading-relaxed">{paragraph.trim()}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No se especificaron instrucciones</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Nutrition Tip */}
        <Card className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              Consejo del Chef
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-amber-100/50 p-4 rounded-lg">
              <p className="text-amber-800 font-medium mb-1">💡 Tip Nutricional:</p>
              <p className="text-amber-700">
                {recipe.difficulty === Difficulty.FÁCIL
                  ? "Las recetas fáciles son perfectas para mantener una alimentación saludable sin complicaciones. Prepara porciones extras para tener comidas listas."
                  : recipe.difficulty === Difficulty.MEDIO
                    ? "Cocinar recetas de dificultad media te permite explorar nuevas técnicas. Organiza todos los ingredientes antes de empezar para mayor eficiencia."
                    : "Las recetas complejas combinan múltiples técnicas para resultados excepcionales. Lee toda la receta antes de empezar y prepara un timeline."
                }
              </p>
            </div>
            {recipe.tags?.includes(Tag.VEGANA) && (
              <div className="bg-green-100/50 p-4 rounded-lg">
                <p className="text-green-800 font-medium mb-1">🌱 Receta Vegana:</p>
                <p className="text-green-700">
                  Esta receta 100% vegetal aporta fibra, antioxidantes y fitoquímicos beneficiosos para la salud.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add to Menu Dialog */}
      {isLoggedIn && (
        <AddToWeeklyMenuDialog
          isOpen={addToMenuOpen}
          onClose={() => setAddToMenuOpen(false)}
          recipeTitle={recipe.title}
          recipeId={recipe.id}
        />
      )}
    </div>
  );
};

export default RecipeDetail;